import { useEffectWithCleanup, useEffectWithoutCleanup, useState } from "Shared/haunted/CustomHooks";
import { getParsedProperty, isTabletOrMobileResolution } from "Services/common";
import { HauntedFunc } from "Shared/haunted/HooksHelpers";
import { ApiStationSettings } from "Shared/models/ApiStationSettings";
import { useNavitaireSettings } from "ComponentHelpers/useNavitaireSettings";
import { ApiCultureSettings } from "ComponentModels/ApiCultureSettings";
import classNames from "classnames";
import { usePreviousSearch } from "./searchTab/flights/usePreviousSearch";
import { useReduxState } from "Shared/redux/useReduxState";
import { TabProps } from "../tabs";
import { useMyBookingMobileTab } from "./useMyBookingMobileTab";
import { useAdminTab } from "./adminTab/useAdminTab";
import { useFlightStatusTab } from "./flightStatusTab/useFlightStatusTab";
import { useSmartSearchTab } from "./searchTab/useSmartSearchTab";
import { useWindowSize } from "../../shared/haunted/useWindowSize";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import i18next from "i18next";
import { DynamicSettings } from "Components/admin/DynamicSettings";
import { PUB_SUBS } from "Services/pub-sub-service/PubSub";
import { ref } from "Components/directives/ref";
import { useCurrentSearchManager } from "Shared/managers/current-search-manager/useCurrentSearchManager";
import { useLogger } from "Services/logging/logger/useLogger";
import { HomepageDomLoadedEventData } from "Services/logging/logger/types-and-interfaces/HomepageDomLoadedEvent";
import { useRef } from "haunted";
import { html } from "lit-html";

export const useShadowDOM = false;
export const name = "new-bb-searchbox";

export const observedAttributes: (keyof Attributes)[] = [
	"culture-settings",
	"culture",
	"currency",
	"dynamic-settings",
	"station-settings",
	"timetable-max-months-fallback",
];

export const mobileTabNames = ["smart-search", "my-booking"] as const;
export type MobileTabName = (typeof mobileTabNames)[number];

export const tabNames = ["smart-search", "manage-your-booking", "check-in", "flight-status"] as const;
export type TabName = (typeof tabNames)[number];

export const FIRST_TAB_INDEX = 0;
export const LAST_MOBILE_TAB_INDEX = mobileTabNames.length - 1;

export interface Attributes {
	"culture-settings": string;
	"culture": string;
	"currency": string;
	"dynamic-settings": string;
	"station-settings": string;
	"timetable-max-months-fallback": string;
}

export interface Props {
	culture: string;
	cultureSettings: ApiCultureSettings;
	currency: string;
	dynamicSettings: DynamicSettings;
	stationSettings: ApiStationSettings;
}

export const Component: HauntedFunc<Props> = (host) => {
	const props: Props = {
		culture: host.culture,
		cultureSettings:
			host.cultureSettings && typeof host.cultureSettings === "string"
				? getParsedProperty<ApiCultureSettings>(host.cultureSettings)
				: undefined,
		currency: host.currency,
		dynamicSettings:
			host.dynamicSettings && typeof host.dynamicSettings === "string"
				? getParsedProperty<DynamicSettings>(host.dynamicSettings)
				: undefined,
		stationSettings:
			host.stationSettings && typeof host.stationSettings === "string"
				? getParsedProperty<ApiStationSettings>(host.stationSettings)
				: undefined,
	};

	const windowSize = useWindowSize();

	const desktopContainer = useRef<HTMLDivElement>(undefined);

	const [userInfo] = useReduxState("userInfo");
	const [kontent] = useReduxState("kontent");

	const navitaireSettings = useNavitaireSettings();
	const { logEvent } = useLogger();

	const [selectedMobileTab, setSelectedMobileTab] = useState<MobileTabName>("smart-search");
	const [selectedTab, setSelectedTab] = useState<TabName>("smart-search");

	const previousSearch = usePreviousSearch();

	const currentSearchManager = useCurrentSearchManager();

	const logHomePageLoaded = () => {
		// DEVNOTE This is here because JET-9907
		logEvent<HomepageDomLoadedEventData>("homepage_dom_loaded", { userInfo });
	};

	const scrollToContainer = () => {
		if (isTabletOrMobileResolution()) return;

		if (desktopContainer.current) {
			const element = desktopContainer.current;
			const rect = element.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			const DROPDOWN_MAX_HEIGHT = 400;

			if (rect.bottom + DROPDOWN_MAX_HEIGHT > viewportHeight) {
				element.scrollIntoView({
					behavior: "auto",
					block: "start",
					inline: "center",
				});
			}
		}
	};

	const subscribe = () => {
		const handler = PUB_SUBS.StationSelectorOpened.subscribe(scrollToContainer);
		return () => handler.unsubscribe();
	};

	const init = async () => {
		if (!userInfo?.RoleCode) return;

		logHomePageLoaded();

		previousSearch.refresh();

		await navitaireSettings.init({
			culture: props.culture,
			loadedValues: {
				abTestSettings: window.JetSmart.AbTestSettings,
				cultureSettings: props.cultureSettings,
				currency: props.currency,
				stationSettings: props.stationSettings,
			},
		});
	};

	useEffectWithCleanup(subscribe, []);

	useEffectWithoutCleanup(init, [userInfo?.RoleCode]);

	const tabHeaderTemplate = (isSelected: boolean, label: string, icon: string, isMobile = false) => html`
		<div class="flex h-[56px] max-w-fit items-center justify-center text-base">
			<span
				class=${classNames("common-transition bg-white px-4 flex items-center", {
					"py-3 text-[#636363] rounded-xl font-normal": !isSelected,
					"py-5 text-n-blue rounded-t-lg rounded-b-none font-bold max-h-full": isSelected,
					"border-b-0 sm:border-b-[3px] sm:border-b-n-cyan sm:border-x-0 sm:border-t-0 sm:border-solid":
						isSelected && !isMobile,
				})}
			>
				<span class="mr-2">${unsafeHTML(icon)}</span>
				<span> ${unsafeHTML(label)} </span>
			</span>
		</div>
	`;

	const smartSearch = useSmartSearchTab({
		currentSearchManager,
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
	});

	const myBookingMobileTab = useMyBookingMobileTab({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isUsingAmericanMiles: currentSearchManager.currentSearch.useAmericanMiles,
	});

	const manageYourBookingTab = useAdminTab({
		culture: props.culture,
		isActive: windowSize.size === "XS" ? false : selectedTab === "manage-your-booking",
		type: "manage",
	});

	const checkInTab = useAdminTab({
		culture: props.culture,
		isActive: windowSize.size === "XS" ? false : selectedTab === "check-in",
		type: "check-in",
	});

	const flightStatusTab = useFlightStatusTab({
		culture: props.culture,
		dynamicSettings: props.dynamicSettings,
		isActive: windowSize.size === "XS" ? false : selectedTab === "flight-status",
		isUsingAmericanMiles: currentSearchManager.currentSearch.useAmericanMiles,
	});

	const desktopTabsTemplate = () => {
		const dto: TabProps<TabName> = {
			data: [
				{
					index: 0,
					name: "smart-search",
					contentTemplate: smartSearch.htmlTemplate,
					tabHeaderTemplate: () =>
						tabHeaderTemplate(
							selectedTab === "smart-search",
							kontent?.searchbox.searchboxFlightsSection.contents.searchboxSectionTitle.value,
							kontent?.searchbox.searchboxFlightsSection.contents.searchboxSectionIcon.value
						),
				},
				{
					index: 1,
					name: "manage-your-booking",
					contentTemplate: manageYourBookingTab.htmlTemplate,
					tabHeaderTemplate: () =>
						tabHeaderTemplate(
							selectedTab === "manage-your-booking",
							kontent?.manageYourFlight.manageYourFlightTest.contents.searchboxTitle.value,
							kontent?.manageYourFlight.manageYourFlightTest.contents.searchboxIcon.value
						),
				},
				{
					index: 2,
					name: "check-in",
					contentTemplate: checkInTab.htmlTemplate,
					tabHeaderTemplate: () =>
						tabHeaderTemplate(
							selectedTab === "check-in",
							kontent?.checkIn.checkInTest.contents.searchboxTitle.value,
							kontent?.checkIn.checkInTest.contents.searchboxIcon.value
						),
				},
				{
					index: 3,
					name: "flight-status",
					contentTemplate: flightStatusTab.htmlTemplate,
					tabHeaderTemplate: () =>
						tabHeaderTemplate(
							selectedTab === "flight-status",
							kontent?.flightStatus.flightStatusTest.contents.searchboxTitle.value,
							kontent?.flightStatus.flightStatusTest.contents.searchboxIcon.value
						),
				},
			],
			selectedTab,
			onSelect: setSelectedTab,
		};

		return html`
			<div ref=${ref(desktopContainer)} class="relative z-10 mt-[-72px] flex w-full justify-center">
				<dc-tabs class="container my-4" .dto=${dto}> </dc-tabs>
			</div>
		`;
	};

	const mobileTabsTemplate = () => {
		const dto: TabProps<MobileTabName> = {
			data: [
				{
					index: 0,
					name: "smart-search",
					contentTemplate: smartSearch.htmlTemplate,
					onClick: myBookingMobileTab.close,
					tabHeaderTemplate: () =>
						tabHeaderTemplate(
							selectedMobileTab === "smart-search",
							kontent?.searchbox.searchboxFlightsSection.contents.searchboxSectionTitle.value,
							kontent?.searchbox.searchboxFlightsSection.contents.searchboxSectionIcon.value,
							true
						),
				},
				{
					index: 1,
					name: "my-booking",
					contentTemplate: myBookingMobileTab.htmlTemplate,
					tabHeaderTemplate: () =>
						tabHeaderTemplate(
							selectedMobileTab === "my-booking",
							i18next.t("Mi Reserva"),
							kontent?.checkIn.checkInTest.contents.searchboxIcon.value,
							true
						),
				},
			],
			selectedTab: selectedMobileTab,
			onSelect: setSelectedMobileTab,
		};

		return html`
			<div class="absolute inset-x-4 top-4 z-10 justify-center">
				<dc-tabs class="my-4 w-full max-w-[95%]" .dto=${dto}> </dc-tabs>
			</div>
		`;
	};

	return windowSize.size === "XS" ? mobileTabsTemplate() : desktopTabsTemplate();
};
